import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Lock } from "lucide-react";
import StageShell from "@/components/StageShell";
import ResumePreview from "@/components/ResumePreview";
import CopyKit from "@/components/kit/CopyKit";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { api, errMsg, exportUrl } from "@/lib/api";

const EXPORTS = [
  { kind: "docx", label: "ATS DOCX", id: "export-docx-button" }, { kind: "pdf", label: "Text PDF", id: "export-pdf-button" },
  { kind: "keywords", label: "Keyword bank CSV", id: "export-keywords-button" }, { kind: "bullets", label: "Bullet bank CSV", id: "export-bullets-button" },
  { kind: "linkedin", label: "LinkedIn headline & About", id: "export-linkedin-button" }, { kind: "skills", label: "Skills-to-build note", id: "export-skills-button" },
];
const inp = "w-full bg-slate-900 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500";

function Score({ label, value, sub }) {
  return <div className="card-surface py-3"><div className="label-xs">{label}</div><div className="text-2xl font-bold font-display mt-1">{value}</div><div className="text-xs text-muted-foreground">{sub}</div></div>;
}

function ApprovalForm({ job, onDone }) {
  const [f, setF] = useState({ approved_by: job.client.name, date: new Date().toISOString().slice(0, 10), note: "", voice_link: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try { await api.post(`/jobs/${job.id}/client-approval`, f); toast.success("Client approval recorded. Locked as v1."); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (job.client_approval) {
    const a = job.client_approval;
    return <div className="card-surface text-sm" data-testid="client-approval-record"><div className="flex items-center gap-2 text-emerald-300 font-semibold mb-1"><Lock size={14} /> Locked as v1</div>
      Approved by {a.approved_by} on {a.date}. Recorded by {a.recorded_by}.{a.note && <div className="text-slate-400 mt-1">"{a.note}"</div>}{a.voice_link && <a href={a.voice_link} className="text-blue-400 text-xs" target="_blank" rel="noreferrer">Voice note</a>}</div>;
  }
  return (
    <div className="card-surface space-y-3" data-testid="client-approval-form">
      <div className="font-semibold">Record client approval</div>
      <div className="grid grid-cols-2 gap-3">
        <input data-testid="approval-by-input" className={inp} value={f.approved_by} onChange={set("approved_by")} placeholder="Approved by" />
        <input data-testid="approval-date-input" type="date" className={inp} value={f.date} onChange={set("date")} />
      </div>
      <input data-testid="approval-voice-input" className={inp} value={f.voice_link} onChange={set("voice_link")} placeholder="Voice-note link (optional)" />
      <textarea data-testid="approval-note-input" className={inp} value={f.note} onChange={set("note")} placeholder="Note" />
      <Button data-testid="approval-submit-button" onClick={submit} className="bg-blue-600 hover:bg-blue-500 text-white">Record approval & lock v1</Button>
    </div>
  );
}

export default function Stage10({ job, refresh }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/jobs/${job.id}/summary`).then(({ data }) => setS(data)).catch((e) => setErr(errMsg(e))), [job.id]);
  useEffect(() => { load(); }, [load, job]);
  if (err && !s) return <div className="card-surface text-sm text-rose-300" data-testid="stage10-error">Could not load the review: {err}</div>;
  if (!s) return null;
  const d = s.stage5;
  return (
    <StageShell job={job} stage={10} refresh={refresh}>
      <Tabs defaultValue="copy" data-testid="stage10-tabs">
      <TabsList className="mb-4">
        <TabsTrigger value="copy" data-testid="tab-copy-kit">Copy for Jobright</TabsTrigger>
        <TabsTrigger value="review" data-testid="tab-review">Review, approve & download</TabsTrigger>
      </TabsList>
      <TabsContent value="copy"><CopyKit job={job} /></TabsContent>
      <TabsContent value="review" className="space-y-5">
      <div className="grid grid-cols-5 gap-3" data-testid="stage-scores-summary">
        <Score label="0 · Facts" value={s.stage0.verified_numeric} sub={`verified numbers · ${s.stage0.roles} roles`} />
        <Score label="1 · JDs" value={s.stage1.usable} sub={`usable · ${s.stage1.duplicate} dupes removed`} />
        <Score label="2 · Keywords" value={s.stage2.must_haves} sub={`must-haves of ${s.stage2.keywords}`} />
        <Score label="3 · Gap" value={`${s.stage3.has_shows}/${s.stage3.has_hidden}/${s.stage3.missing}`} sub="shows / hidden / missing" />
        <Score label="5 · Coverage" value={d ? `${d.coverage_pct}%` : "-"} sub={d ? `${d.bullets_with_numbers_pct}% bullets with numbers` : ""} />
        <Score label="4 · Angle" value={s.stage4 ? "✓" : "-"} sub={s.stage4?.headline_title} />
        <Score label="6 · Panel" value={s.stage6?.scores ? Math.min(...Object.values(s.stage6.scores)) : "-"} sub={s.stage6 ? `lowest agent score · round ${s.stage6.round}` : "not run"} />
        <Score label="7 · ATS" value={s.stage7 ? `${s.stage7.match_pct}%` : "-"} sub={s.stage7 ? `match · ${s.stage7.booleans_passed}/${s.stage7.booleans} Boolean pass` : "not run"} />
        <Score label="8 · AI-tell" value={s.stage8 ? `${s.stage8.before_score} → ${s.stage8.after_score}` : "-"} sub="% lines flagged" />
        <Score label="9 · Facts" value={s.stage9 ? s.stage9.untraceable : "-"} sub={s.stage9 ? `untraceable of ${s.stage9.items} checked` : "not run"} />
      </div>
      <div className="grid grid-cols-[1fr_1fr_340px] gap-5 items-start" data-testid="approval-diff-preview">
        <div className="card-surface"><div className="label-xs mb-2">Old resume</div><pre className="whitespace-pre-wrap text-xs font-mono text-slate-300 max-h-[900px] overflow-auto scroll-thin">{s.original_text}</pre></div>
        <div><div className="label-xs mb-2">New base resume · {s.version?.label}</div>{s.content ? <ResumePreview c={s.content} /> : <div className="card-surface text-sm text-muted-foreground">No draft yet.</div>}</div>
        <div className="space-y-4">
          <ApprovalForm job={job} onDone={() => { refresh(); load(); }} />
          <div className="card-surface space-y-2">
            <div className="label-xs mb-1">Export</div>
            {EXPORTS.map((e) => (
              <a key={e.kind} data-testid={e.id} href={s.content ? exportUrl(job.id, e.kind) : undefined}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-border hover:border-blue-500/60 hover:bg-blue-500/5 transition-colors ${!s.content ? "pointer-events-none opacity-40" : ""}`}>
                {["docx", "pdf"].includes(e.kind) ? <FileText size={14} className="text-blue-400" /> : <Download size={14} className="text-slate-400" />} {e.label}
              </a>
            ))}
            {!job.client_approval && <div className="text-[11px] text-amber-300">Not client-approved yet, so exports are drafts.</div>}
            <div className="text-[11px] text-slate-400">Word and PDF use your bullet picks from the Copy tab.</div>
          </div>
        </div>
      </div>
      </TabsContent>
      </Tabs>
    </StageShell>
  );
}
